import { Alert, Box, Button, LinearProgress, Paper, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { FC, useEffect, useRef, useState } from 'react'
import { useAuthProvider } from 'react-admin'
import { getUrl } from '../../providers/data'
import authToken from '../../utils/authToken'

type RestoreStatus = 'pending' | 'running' | 'succeeded' | 'failed'

interface RestoreJob {
  id: string
  status: RestoreStatus
  started_by_email: string
  started_at: string | null
  finished_at: string | null
  error: string | null
  bytes_uploaded: number | null
}

const POLL_INTERVAL_MS = 3000

const MongoRestoreSection: FC = () => {
  const authProvider = useAuthProvider()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [job, setJob] = useState<RestoreJob | null>(null)
  const [error, setError] = useState('')
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
  }, [])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setFile(files[0])
      setError('')
      setJob(null)
    }
  }

  const fetchWithAuth = async (path: string, init: RequestInit = {}) => {
    // authToken.getToken() returns the token already prefixed with "Bearer "
    // (see utils/authToken.ts setToken). Do NOT re-add "Bearer " or the API
    // middleware splits at the wrong space and JWT decode fails with
    // "Invalid header padding".
    const token = authToken.getToken() || ''
    const response = await fetch(`${getUrl()}${path}`, {
      ...init,
      headers: { Authorization: token, ...(init.headers || {}) },
    })
    if (!response.ok) {
      if (authProvider?.checkError) {
        await authProvider.checkError({ status: response.status })
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const body = await response.json().catch(() => null)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      throw new Error(JSON.stringify(body?.detail) || `HTTP ${response.status}`)
    }
    return response.json() as Promise<RestoreJob>
  }

  const pollJobStatus = async (jobId: string) => {
    try {
      const updated = await fetchWithAuth(`/admin/mongo-restore/${jobId}`)
      setJob(updated)
      if (updated.status === 'pending' || updated.status === 'running') {
        pollTimerRef.current = setTimeout(() => void pollJobStatus(jobId), POLL_INTERVAL_MS)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Onbekende fout'
      setError(`Polling mislukt: ${message}`)
    }
  }

  const handleRestore = async () => {
    if (!file) return
    setUploading(true)
    setError('')
    setJob(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      const created = await fetchWithAuth('/admin/mongo-restore', {
        method: 'POST',
        body: formData,
      })
      setJob(created)
      pollTimerRef.current = setTimeout(() => void pollJobStatus(created.id), POLL_INTERVAL_MS)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Onbekende fout'
      setError(`Restore starten mislukt: ${message}`)
    } finally {
      setUploading(false)
    }
  }

  const sizeMb = file ? (file.size / 1024 / 1024).toFixed(1) : null
  const isInFlight = job && (job.status === 'pending' || job.status === 'running')
  const isTerminal = job && (job.status === 'succeeded' || job.status === 'failed')

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        MongoDB data restore
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Upload een gzipped tar (mongodump output, bv. uit <code>app/data/mongo/db-dump</code>) om
        de prognose MongoDB collecties te vervangen. Gebruikt <code>mongorestore --drop --gzip</code> —
        bestaande collecties worden overschreven.
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Tijdens de restore is de prognose-data tijdelijk leeg. Slechts één restore tegelijk wordt
        geaccepteerd (cluster-wide), volgende uploads krijgen 409 totdat de huidige klaar is.
      </Alert>

      <Button
        variant="outlined"
        component="label"
        startIcon={<UploadFileIcon />}
        fullWidth
        sx={{ mb: 2 }}
        disabled={uploading || Boolean(isInFlight)}
      >
        {file ? `${file.name} (${sizeMb} MB)` : 'Selecteer dump.tar.gz'}
        <input type="file" hidden accept=".gz,.tgz,.tar" onChange={handleFileChange} />
      </Button>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>Uploaden naar server…</Typography>
          <LinearProgress />
        </Box>
      )}

      {isInFlight && job && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Job <code>{job.id.slice(0, 8)}</code> — status <strong>{job.status}</strong>
            {job.started_at && ` sinds ${new Date(job.started_at).toLocaleTimeString('nl-NL')}`}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {isTerminal && job?.status === 'succeeded' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Restore voltooid (job <code>{job.id.slice(0, 8)}</code>).
        </Alert>
      )}

      {isTerminal && job?.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Restore mislukt (job <code>{job.id.slice(0, 8)}</code>): {job.error}
        </Alert>
      )}

      <Button
        onClick={() => void handleRestore()}
        variant="contained"
        color="warning"
        disabled={!file || uploading || Boolean(isInFlight)}
      >
        Restore starten
      </Button>
    </Paper>
  )
}

export default MongoRestoreSection
